import { useCallback, useEffect, useRef, useState } from 'react';

/** The only part of a drag event this hook reads. Structurally satisfied by React.DragEvent / DragEvent. */
export interface DragLike {
    dataTransfer: { readonly types: readonly string[] } | null;
}

export interface FilesDragOver {
    isDragOver: boolean;
    /** Spread into `getRootProps(...)` (they compose with react-dropzone's own handlers). */
    rootHandlers: {
        onDragEnter: (event: DragLike) => void;
        onDragLeave: (event: DragLike) => void;
        onDrop: (event: DragLike) => void;
    };
}

export function hasFiles(dataTransfer: { readonly types: readonly string[] } | null | undefined): boolean {
    return !!dataTransfer && Array.from(dataTransfer.types).includes('Files');
}

/** A dragleave whose pointer is at/outside the viewport edge means the drag left the document. */
export function leftViewport(point: { clientX: number; clientY: number }, width: number, height: number): boolean {
    return point.clientX <= 0 || point.clientY <= 0 || point.clientX >= width || point.clientY >= height;
}

/**
 * Whether a FILE drag is currently over the editor root, for the "Drop files to attach" overlay.
 *
 * Why not react-dropzone's `isDragActive`: it keeps a list of drag targets and, on every dragleave,
 * drops the ones for which `root.contains(target)` is false. The Lexical editor is portaled out of
 * the root's DOM subtree (LexicalEditorCore slots it into the shadow host's light DOM), so once the
 * pointer entered the editor the list emptied on the next dragleave and the overlay switched off
 * while still over the editor, then back on over the toolbar: the flicker.
 *
 * Here we count enter/leave events that reach the root (React bubbles portal events through the
 * React tree) and never inspect the target. Browsers fire dragenter(new) before dragleave(old), so
 * the depth only reaches 0 when the pointer has left every child.
 */
export function useFilesDragOver(): FilesDragOver {
    const depth = useRef(0);
    const [isDragOver, setIsDragOver] = useState(false);

    const reset = useCallback(() => {
        depth.current = 0;
        setIsDragOver(false);
    }, []);

    const onDragEnter = useCallback((event: DragLike) => {
        if (!hasFiles(event.dataTransfer)) return;
        depth.current += 1;
        setIsDragOver(true);
    }, []);

    const onDragLeave = useCallback((event: DragLike) => {
        if (!hasFiles(event.dataTransfer)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setIsDragOver(false);
    }, []);

    const onDrop = useCallback(() => reset(), [reset]);

    // A drop, an aborted drag, or a drag that leaves the document may never deliver a dragleave to the
    // root; don't leave the overlay stuck.
    useEffect(() => {
        const onWindowDragLeave = (event: Event) => {
            if (event instanceof MouseEvent && leftViewport(event, window.innerWidth, window.innerHeight)) reset();
        };
        window.addEventListener('drop', reset, true);
        window.addEventListener('dragend', reset, true);
        window.addEventListener('dragleave', onWindowDragLeave, true);
        return () => {
            window.removeEventListener('drop', reset, true);
            window.removeEventListener('dragend', reset, true);
            window.removeEventListener('dragleave', onWindowDragLeave, true);
        };
    }, [reset]);

    return { isDragOver, rootHandlers: { onDragEnter, onDragLeave, onDrop } };
}
